/**
 * /app/symbols — catalog of all tradeable symbols the engine watches.
 *
 * One screen, no extra detail page (per-symbol detail is a side drawer
 * — cheaper UX than a separate route and same info density).
 *
 * Filters: search, active only, with my open positions, with signals
 * in the last 24h. Sort: signals_24h DESC by default.
 *
 * Click a row → SymbolDetailDrawer with last_seen, 24h signal breakdown
 * by type, link to /app/signals?symbol=… and /app/positions?symbol=….
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Drawer } from '@/components/ui/Drawer'
import { Input } from '@/components/ui/Input'
import { Pagination } from '@/components/ui/Pagination'
import { Select } from '@/components/ui/Select'
import {
  type SymbolRow,
  type SymbolsFilters,
  useSymbolDetail,
  useSymbols,
} from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import { formatNumber, formatUsd, timeAgo } from '@/lib/format'

const PAGE_SIZE = 100

export default function Symbols() {
  const { t } = useTranslation('app')

  const [search, setSearch] = useState('')
  const [scope, setScope] = useState<'all' | 'active' | 'positions' | 'signals'>('all')
  const [offset, setOffset] = useState(0)
  const [openDetail, setOpenDetail] = useState<string | null>(null)

  const filters: SymbolsFilters = useMemo(() => ({
    limit: PAGE_SIZE,
    offset,
    search: search.trim() || undefined,
    active_only:    scope === 'active',
    with_positions: scope === 'positions',
    with_signals:   scope === 'signals',
  }), [search, scope, offset])

  const { data, isLoading, isFetching } = useSymbols(filters)
  const reset = () => setOffset(0)

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('symbols.title')}
        </h1>
        <p className="text-sm text-textDim mt-1">{t('symbols.subtitle')}</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <Input
          placeholder={t('symbols.search_placeholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); reset() }}
        />
        <Select
          value={scope}
          onChange={(e) => {
            setScope(e.target.value as 'all' | 'active' | 'positions' | 'signals')
            reset()
          }}
        >
          <option value="all">{t('symbols.scope.all')}</option>
          <option value="active">{t('symbols.scope.active')}</option>
          <option value="positions">{t('symbols.scope.with_positions')}</option>
          <option value="signals">{t('symbols.scope.with_signals')}</option>
        </Select>
        <div className="text-xs text-textFaint flex items-center px-2">
          {data ? t('symbols.total', { count: data.total }) : ''}
        </div>
      </div>

      <section className="bg-ink border border-line">
        {isLoading || !data ? (
          <div className="px-5 py-10 text-center text-textFaint text-sm">—</div>
        ) : data.items.length === 0 ? (
          <div className="px-5 py-10 text-center text-textFaint text-sm">
            {t('symbols.empty')}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-textDim text-xs uppercase tracking-wider border-b border-line bg-elevated/40">
                  <tr>
                    <Th className="text-left px-5">{t('symbols.table.symbol')}</Th>
                    <Th className="text-left">{t('symbols.table.exchange')}</Th>
                    <Th className="text-left">{t('symbols.table.status')}</Th>
                    <Th className="text-right">{t('symbols.table.oi_min')}</Th>
                    <Th className="text-right">{t('symbols.table.vol24')}</Th>
                    <Th className="text-right">{t('symbols.table.signals_24h')}</Th>
                    <Th className="text-right">{t('symbols.table.positions_open')}</Th>
                    <Th className="text-right">{t('symbols.table.last_signal')}</Th>
                    <Th className="text-right px-5">{t('symbols.table.last_seen')}</Th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {data.items.map((s) => (
                    <SymbolRowView
                      key={`${s.exchange}-${s.symbol}`}
                      row={s}
                      onClick={() => setOpenDetail(s.symbol)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
              loading={isFetching}
            />
          </>
        )}
      </section>

      <SymbolDetailDrawer
        symbol={openDetail}
        onClose={() => setOpenDetail(null)}
      />
    </div>
  )
}

/* ─── Row ───────────────────────────────────────────────────────── */

function SymbolRowView({
  row, onClick,
}: {
  row: SymbolRow
  onClick: () => void
}) {
  const { t } = useTranslation('app')
  return (
    <tr
      onClick={onClick}
      className={cn(
        'border-b border-line/40 hover:bg-elevated/30 cursor-pointer transition-colors',
        row.open_positions > 0 && 'bg-fire-faint/20',
      )}
    >
      <td className="px-5 py-2 font-semibold">{row.symbol}</td>
      <td className="py-2 text-textDim uppercase">{row.exchange}</td>
      <td className="py-2">
        {row.is_active ? (
          <span className="pill bg-pulse-faint text-pulse">{t('symbols.status.active')}</span>
        ) : (
          <span className="pill bg-elevated text-textFaint">{t('symbols.status.inactive')}</span>
        )}
      </td>
      <td className="text-right py-2 text-textDim">
        {row.min_oi_usd != null ? formatUsd(row.min_oi_usd, { compact: true }) : '—'}
      </td>
      <td className="text-right py-2 text-textDim">
        {row.avg_volume_24h != null ? formatUsd(row.avg_volume_24h, { compact: true }) : '—'}
      </td>
      <td className={cn('text-right py-2', row.signals_24h > 0 ? 'text-text' : 'text-textFaint')}>
        {row.signals_24h}
      </td>
      <td className={cn('text-right py-2', row.open_positions > 0 ? 'text-fire' : 'text-textFaint')}>
        {row.open_positions}
      </td>
      <td className="text-right py-2 text-textFaint">
        {row.last_signal_at ? timeAgo(row.last_signal_at) : '—'}
      </td>
      <td className="text-right px-5 py-2 text-textFaint">
        {row.last_seen ? timeAgo(row.last_seen) : '—'}
      </td>
    </tr>
  )
}

/* ─── Detail drawer ─────────────────────────────────────────────── */

function SymbolDetailDrawer({
  symbol, onClose,
}: {
  symbol: string | null
  onClose: () => void
}) {
  const { t } = useTranslation('app')
  const { data, isLoading, error } = useSymbolDetail(symbol)

  return (
    <Drawer
      open={!!symbol}
      onClose={onClose}
      title={data ? data.symbol : symbol ?? '—'}
      subtitle={data ? `${data.exchange.toUpperCase()} · ${data.is_active ? t('symbols.status.active') : t('symbols.status.inactive')}` : undefined}
    >
      {isLoading || !data ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">
          {error ? t('symbols.error') : '—'}
        </div>
      ) : (
        <div className="px-5 py-4 space-y-6 font-mono">
          <div className="grid grid-cols-2 gap-3">
            <KpiBox label={t('symbols.table.oi_min')}
                    value={data.min_oi_usd != null ? formatUsd(data.min_oi_usd, { compact: true }) : '—'} />
            <KpiBox label={t('symbols.table.vol24')}
                    value={data.avg_volume_24h != null ? formatUsd(data.avg_volume_24h, { compact: true }) : '—'} />
            <KpiBox label={t('symbols.table.signals_24h')}
                    value={formatNumber(data.signals_24h, 0)} />
            <KpiBox label={t('symbols.table.positions_open')}
                    value={formatNumber(data.open_positions_count, 0)} />
          </div>

          {data.signal_types_24h.length > 0 && (
            <Section title={t('symbols.signal_breakdown_24h')}>
              <ul className="divide-y divide-line/40">
                {data.signal_types_24h.map((s) => (
                  <li key={s.type} className="flex items-center px-3 py-2 text-xs">
                    <span className="text-textDim">
                      {t(`signal_type.${s.type}`, { defaultValue: s.type })}
                    </span>
                    <span className="ml-auto tabular-nums">{s.count}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section title={t('symbols.activity')}>
            <Row label={t('symbols.table.last_signal')}
                 value={data.last_signal_at
                   ? `${new Date(data.last_signal_at).toLocaleString()} · ${timeAgo(data.last_signal_at)}`
                   : '—'} />
            <Row label={t('symbols.table.last_seen')}
                 value={data.last_seen
                   ? `${new Date(data.last_seen).toLocaleString()} · ${timeAgo(data.last_seen)}`
                   : '—'} />
          </Section>

          <div className="flex gap-2 text-xs">
            <Link
              to={`/app/signals`}
              className="pill bg-elevated text-textDim hover:text-text"
              onClick={onClose}
            >
              {t('symbols.link_signals')}
            </Link>
            <Link
              to={`/app/positions`}
              className="pill bg-elevated text-textDim hover:text-text"
              onClick={onClose}
            >
              {t('symbols.link_positions')}
            </Link>
          </div>
        </div>
      )}
    </Drawer>
  )
}

/* ─── building blocks ─────────────────────────────────────────────── */

function Th({ className, children }: { className?: string; children: React.ReactNode }) {
  return <th className={cn('py-2 font-medium', className)}>{children}</th>
}

function KpiBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-ink p-3 border border-line">
      <div className="text-textFaint text-2xs uppercase tracking-wider">
        {label}
      </div>
      <div className="mt-1 text-sm tabular-nums">{value}</div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-textFaint text-xs uppercase tracking-wider mb-2">
        {title}
      </h3>
      <div className="border border-line bg-ink">{children}</div>
    </section>
  )
}

function Row({
  label, value, valueClass,
}: {
  label: string
  value: string
  valueClass?: string
}) {
  return (
    <div className="flex items-center px-3 py-2 text-xs">
      <span className="text-textDim">{label}</span>
      <span className={cn('ml-auto tabular-nums', valueClass ?? 'text-text')}>
        {value}
      </span>
    </div>
  )
}
